import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  RefreshCw,
  Eye,
  FileText,
  Check,
  X,
  Inbox as InboxIcon,
  Settings,
  ExternalLink,
  Link2,
  MessageSquare,
  AlertTriangle,
  GripVertical,
  Search,
  Import,
  ListChecks,
  Paperclip,
  Image,
  File,
  Download,
  HelpCircle,
  Info,
  Trash2,
  Loader2,
  MailOpen,
  Circle,
  CheckCircle2,
  Flag,
  Star,
  ChevronDown,
  Minus,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  EyeOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  PageHeader, 
  DataTable, 
  StatusBadge as SharedStatusBadge,
  LoadingSpinner,
  EmptyState 
} from '../components/shared/DataComponents';
import { useTableDisplaySettings, getZoomClasses } from '../hooks/useZoom';
import { ZoomControl } from '../components/common/ZoomControl';
import PageZoomContainer from '../components/layout/PageZoomContainer';
import { ShiftImportForm } from '../components/inbox/ShiftImportForm';
import { MultiShiftImportForm } from '../components/inbox/MultiShiftImportForm';
import { EmailAttachmentManager } from '../components/admin/EmailAttachmentManager';
// EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: Use V2 workspace
import DocumentReviewWorkspace from '../components/admin/DocumentReviewWorkspaceV2';
// EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Import format guidance
import ImportFormatHintPanel from '../components/guidance/ImportFormatHintPanel';
// EF-AI-EMAIL-INTERPRETER-V1: AI-powered email parsing
import AIInterpreterPanel from '../components/inbox/AIInterpreterPanel';
import { Bot, Sparkles } from 'lucide-react';
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from '../components/common/GuideButton';
// EVENTSFLO-INBOX-PDF-INVOICE-REVIEW-UI-V1 (Phase 3)
import InvoiceImportReviewDialog from '../components/inbox/InvoiceImportReviewDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// EVENTSFLO-INBOX-DOCUMENT-VIEWER-IMAGE-REGRESSION-V1
// The attachment file endpoint now requires auth (EF-DOCUMENT-STORAGE-SAFE-REMEDIATION-V1).
// <img src> and window.open cannot send Authorization headers, so we append the JWT
// via the backend-supported ?token=<jwt> query param.
const withInboxAttachmentToken = (url) => {
  if (!url) return url;
  let token = '';
  try { token = localStorage.getItem('eventsflo_auth_token') || ''; } catch { /* noop */ }
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
};

// EF-INBOX-UX-REFACTOR-V1: Flag color configuration
const FLAG_COLORS = {
  none: { color: 'text-zinc-500', bgHover: 'hover:bg-zinc-500/10', label: 'No Flag' },
  black: { color: 'text-zinc-900 dark:text-zinc-100', bgHover: 'hover:bg-zinc-500/10', label: 'Unread' },
  grey: { color: 'text-zinc-400', bgHover: 'hover:bg-zinc-500/10', label: 'Neutral' },
  red: { color: 'text-red-500', bgHover: 'hover:bg-red-500/10', label: 'Project Request' },
  green: { color: 'text-emerald-500', bgHover: 'hover:bg-emerald-500/10', label: 'Crew Onboarding' },
  blue: { color: 'text-blue-500', bgHover: 'hover:bg-blue-500/10', label: 'Important' },
  yellow: { color: 'text-amber-500', bgHover: 'hover:bg-amber-500/10', label: 'Follow-up' },
};

const FLAG_CYCLE_ORDER = ['none', 'red', 'green', 'blue', 'yellow', 'grey'];

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }) + ', ' + date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Status badge for inbox messages
const InboxStatusBadge = ({ status }) => {
  const statusStyles = {
    new: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    reviewed: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    processed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    dismissed: 'bg-surface-elevated/50 text-muted-foreground border-border',
  };
  
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${statusStyles[status] || statusStyles.dismissed}`}>
      {status}
    </span>
  );
};

// Type badge for email prefixes
const TypeBadge = ({ prefix, reference }) => {
  if (!prefix && !reference) return <span className="text-muted-foreground">-</span>;
  
  return (
    <div className="flex flex-col gap-0.5">
      {prefix && (
        <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] w-fit">
          {prefix}
        </Badge>
      )}
      {reference && (
        <span className="text-[10px] text-muted-foreground">{reference}</span>
      )}
    </div>
  );
};

export default function InboxPage() {
  const navigate = useNavigate();
  
  // Use shared zoom/wrap hook for consistent behavior across pages
  const { 
    currentZoom, scale, zoomIn, zoomOut, canZoomIn, canZoomOut,
    wrapText, toggleWrap 
  } = useTableDisplaySettings('inbox-page');
  const zoomClasses = getZoomClasses(scale);
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('messages'); // 'messages' or 'attachments'
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, message: null });
  const [searchTerm, setSearchTerm] = useState('');
  
  // EF-EMAIL-INBOX-STABILISATION-V1: Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, message: null });
  const [deleting, setDeleting] = useState(false);

  // EVENTSFLO-PROJECT-INTAKE-PASTED-TEXT-INTO-EXISTING-PROJECT-FLOW-V1
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pasteSubject, setPasteSubject] = useState('');
  const [pasteSource, setPasteSource] = useState('WhatsApp');
  const [pasteText, setPasteText] = useState('');
  const [pasting, setPasting] = useState(false);
  
  // EF-CREW-DOCUMENT-INGESTION-FIX-V1: Document Review Workspace state
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [selectedAttachmentForReview, setSelectedAttachmentForReview] = useState(null);
  const [allMessageAttachments, setAllMessageAttachments] = useState([]);
  
  // EF-INBOX-V1-STEP2: Track if user has ever checked mail (for first-time guidance)
  const [hasEverFetched, setHasEverFetched] = useState(() => {
    return localStorage.getItem('inbox-has-fetched') === 'true';
  });
  
  // EF-EMAIL-INBOX-AUTO-SYNC-V1: Auto-sync state
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'syncing' | 'success' | 'error'
  const [syncError, setSyncError] = useState(null);
  const syncLockRef = useRef(false);
  const pollingIntervalRef = useRef(null);
  const POLLING_INTERVAL = 90000; // 90 seconds
  
  // Thread duplicate warning state
  const [threadWarning, setThreadWarning] = useState(null);
  
  // EF-INBOX-SHIFT-IMPORT-WORKFLOW-V1: Import form state
  const [showImportForm, setShowImportForm] = useState(false);
  const [importParsedData, setImportParsedData] = useState(null);
  const [importMessageId, setImportMessageId] = useState(null);
  
  // EF-SHIFT-IMPORT-MULTI-SHIFT-UI-V1: Multi-shift import form state
  const [showMultiShiftImportForm, setShowMultiShiftImportForm] = useState(false);
  const [multiShiftParsedData, setMultiShiftParsedData] = useState(null);
  
  // EF-AI-EMAIL-INTERPRETER-V1: AI Interpreter state
  const [showAIInterpreter, setShowAIInterpreter] = useState(false);

  // EVENTSFLO-INBOX-PDF-INVOICE-REVIEW-UI-V1 (Phase 3)
  const [invoiceImportAttachment, setInvoiceImportAttachment] = useState(null);
  const [invoiceImportOpen, setInvoiceImportOpen] = useState(false);
  const [aiInterpreterMessage, setAiInterpreterMessage] = useState(null);
  // EVENTSFLO-AI-PARSER-SELECTION-SCOPE-V1: When the user highlights a portion
  // of the email body before clicking "New AI Draft" / "Interpret with AI",
  // we send only the selection. Otherwise the full body is parsed.
  const [aiInterpreterSelection, setAiInterpreterSelection] = useState('');
  const previewBodyRef = useRef(null);

  // EVENTSFLO-INBOX-INTAKE-STRUCTURE-AND-HELP-V1: in-platform Help drawer
  const [inboxHelpOpen, setInboxHelpOpen] = useState(false);

  // EVENTSFLO-INBOX-BATCH-HIDE-AND-PDF-VIEWER-CONTROLS-V1
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchHiding, setBatchHiding] = useState(false);

  const toggleSelected = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  
  // EF-INBOX-UX-REFACTOR-V1: Font size control for preview
  const [previewFontScale, setPreviewFontScale] = useState(() => {
    const saved = localStorage.getItem('inbox-preview-font-scale');
    return saved ? parseFloat(saved) : 1.0;
  });
  
  // EF-INBOX-UX-REFACTOR-V1: Sorting state
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'flag'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [filterFlag, setFilterFlag] = useState(null); // null = all, or specific color
  
  // Resizable preview pane state
  const [previewWidth, setPreviewWidth] = useState(() => {
    const saved = localStorage.getItem('inbox-preview-width');
    return saved ? parseInt(saved, 10) : 400;
  });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // Load messages
  // EVENTSFLO-INBOX-PAGINATION-AND-HIDDEN-FILTER-V1: offset-based pagination.
  // EVENTSFLO-INBOX-HIDDEN-VIEW-AND-UNHIDE-V1: 'hidden' tab queries with
  // is_hidden=true; visible tabs use the default visible filter.
  const PAGE_SIZE = 50;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [serverStats, setServerStats] = useState(null);

  const fetchPage = useCallback(async (tab, pageOffset) => {
    const qs = new URLSearchParams();
    if (tab === 'hidden') {
      qs.set('is_hidden', 'true');
    } else if (tab && tab !== 'all') {
      qs.set('status', tab);
    }
    qs.set('offset', String(pageOffset));
    qs.set('limit', String(PAGE_SIZE));
    const response = await api.get(`/inbox/messages?${qs.toString()}`);
    const data = response.data || {};
    return {
      messages: Array.isArray(data) ? data : (data.messages || []),
      stats: data.stats || null,
      hasMore: Boolean(data.has_more),
      offset: typeof data.offset === 'number' ? data.offset : pageOffset,
    };
  }, []);

  const loadMessages = useCallback(async (tab = 'all') => {
    setLoading(true);
    try {
      const page = await fetchPage(tab, 0);
      setMessages(page.messages);
      setOffset(page.offset + page.messages.length);
      setHasMore(page.hasMore);
      setServerStats(page.stats);
    } catch (error) {
      toast.error('Failed to load inbox messages');
      setMessages([]);
      setHasMore(false);
      setServerStats(null);
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchPage(activeTab, offset);
      setMessages(prev => {
        const seen = new Set(prev.map(m => m.id));
        const append = page.messages.filter(m => !seen.has(m.id));
        return [...prev, ...append];
      });
      setOffset(page.offset + page.messages.length);
      setHasMore(page.hasMore);
      setServerStats(page.stats);
    } catch (error) {
      toast.error('Failed to load more messages');
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, offset, hasMore, loadingMore, activeTab]);

  useEffect(() => {
    loadMessages(activeTab);
  }, [loadMessages, activeTab]);
  
  // Resizable preview pane handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = containerRect.right - e.clientX;
    const minWidth = 250;  // Minimum preview width
    const maxWidth = containerRect.width * 0.6;  // Maximum 60% of container
    setPreviewWidth(Math.max(minWidth, Math.min(maxWidth, newWidth)));
  }, [isDragging]);
  
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      localStorage.setItem('inbox-preview-width', previewWidth.toString());
    }
  }, [isDragging, previewWidth]);
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  // EVENTSFLO-INBOX-CHECK-MAIL-FAILURE-AUDIT-FIX-V1
  // Categorise the raw backend/IMAP error string into a clear, actionable
  // message. Sensitive values (passwords, full hosts) are NEVER added here —
  // we only echo the host/port/security mode that the backend already
  // logged unredacted.
  const formatInboxSyncError = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return 'Failed to fetch emails. (No detail returned.)';
    const lower = s.toLowerCase();
    if (lower.includes('ef-inbox-tenant-context')) {
      return (
        'Inbox not configured for this tenant. The IMAP provider row is missing a company_id. ' +
        'Open Email Settings → Providers and re-save your provider so it gets attached to your company.'
      );
    }
    if (lower.includes('no imap configuration found')) {
      return 'No IMAP configuration found. Add an IMAP provider in Email Settings.';
    }
    if (lower.includes('incomplete imap configuration')) {
      return (
        'IMAP configuration is incomplete. Check that host, port, username and password are all set ' +
        'in Email Settings → Providers.'
      );
    }
    if (lower.includes('authenticationfailed') || lower.includes('auth failed') || lower.includes('login failed') || lower.includes('invalid credentials') || lower.includes('[auth]')) {
      return (
        'Authentication failed. Your IMAP username or password (or app-password) was rejected. ' +
        'Update credentials in Email Settings → Providers.'
      );
    }
    if (lower.includes('certificate') || lower.includes('ssl') || lower.includes('tls')) {
      return 'TLS/SSL connection failed. Check the security mode and port for your IMAP provider.';
    }
    if (
      lower.includes('connection refused') ||
      lower.includes('name or service not known') ||
      lower.includes('temporary failure in name resolution') ||
      lower.includes('getaddrinfo') ||
      lower.includes('connection timed out') ||
      lower.includes('timed out') ||
      lower.includes('network is unreachable')
    ) {
      return 'Connection failed. Could not reach the IMAP host — check the host, port and security mode in Email Settings.';
    }
    if (
      lower.includes('mailbox does not exist') ||
      lower.includes('no such mailbox') ||
      lower.includes('illegal in state auth') ||
      (lower.includes('select') && lower.includes('failed'))
    ) {
      return 'Mailbox/folder not found or could not be selected. Check the folder name (e.g. "INBOX") in Email Settings.';
    }
    if (lower.includes('failed to search emails')) {
      return 'IMAP server rejected the search request. Try again, or contact support if it persists.';
    }
    // Fall back to the raw backend reason, prefixed so it's obviously the live cause
    return `Inbox sync failed: ${s}`;
  };

  // EF-INBOX-V1-STEP2: Enhanced Check Mail with clear feedback
  // EF-EMAIL-INBOX-SYNC-FIX-V2: Added forceSync option
  // EF-EMAIL-INBOX-AUTO-SYNC-V1: Added sync lock and status tracking
  const handleCheckMail = async (forceSync = false, isAutoSync = false) => {
    // Prevent duplicate concurrent sync calls
    if (syncLockRef.current) {
      if (!isAutoSync) {
        toast.info('Sync already in progress...');
      }
      return;
    }
    
    syncLockRef.current = true;
    setFetching(true);
    setSyncStatus('syncing');
    setSyncError(null);
    
    try {
      const response = await api.post(`/email/fetch-now?force_full_sync=${forceSync}`);
      if (response.data.success) {
        const newCount = response.data.new_count || 0;
        const updatedCount = response.data.updated_count || 0;
        const totalFetched = response.data.total_fetched || 0;
        const attachments = response.data.attachments_extracted || 0;
        
        // Update sync status
        setLastSyncTime(new Date());
        setSyncStatus('success');
        
        // Mark that user has fetched at least once
        localStorage.setItem('inbox-has-fetched', 'true');
        setHasEverFetched(true);
        
        // Only show toast for manual syncs or when there are new emails
        if (!isAutoSync || newCount > 0 || updatedCount > 0) {
          if (newCount > 0 || updatedCount > 0) {
            let message = '';
            if (newCount > 0) {
              message += `${newCount} new email${newCount !== 1 ? 's' : ''} fetched`;
            }
            if (updatedCount > 0) {
              message += (message ? ', ' : '') + `${updatedCount} updated`;
            }
            if (attachments > 0) {
              message += ` (${attachments} attachment${attachments !== 1 ? 's' : ''})`;
            }
            toast.success(message, { duration: 4000 });
          } else if (!isAutoSync) {
            // Only show "up to date" message for manual syncs
            if (totalFetched > 0) {
              toast.info(`Inbox is up to date. ${totalFetched} emails checked.`, { duration: 3000 });
            } else {
              toast.info('No new emails found. Your inbox is up to date.', { duration: 3000 });
            }
          }
        }
        
        // Reload messages if there were changes
        if (newCount > 0 || updatedCount > 0 || !isAutoSync) {
          await loadMessages(activeTab);
        }
      } else {
        // EVENTSFLO-INBOX-CHECK-MAIL-FAILURE-AUDIT-FIX-V1:
        // Surface the REAL backend reason instead of a generic
        // "check your settings" message. The backend returns the actionable
        // detail in `error`; older callers used `message`. Try both.
        const backendErr = response.data.error || response.data.message;
        const friendly = formatInboxSyncError(backendErr);
        setSyncStatus('error');
        setSyncError(friendly);
        if (!isAutoSync) {
          toast.error(friendly, { duration: 8000 });
        }
      }
    } catch (error) {
      console.error('Check mail error:', error);
      // EVENTSFLO-INBOX-CHECK-MAIL-FAILURE-AUDIT-FIX-V1
      const data = error?.response?.data;
      const backendErr =
        data?.error ||
        data?.detail ||
        data?.message ||
        error.message ||
        'Connection failed';
      const friendly = formatInboxSyncError(backendErr);
      setSyncStatus('error');
      setSyncError(friendly);
      if (!isAutoSync) {
        toast.error(friendly, { duration: 8000 });
      }
    } finally {
      setFetching(false);
      syncLockRef.current = false;
    }
  };
  
  // EF-EMAIL-INBOX-AUTO-SYNC-V1: Auto-sync polling effect
  useEffect(() => {
    // Function to check if tab is active
    const isTabActive = () => document.visibilityState === 'visible';
    
    // Start polling
    const startPolling = () => {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      // Set up new polling interval
      pollingIntervalRef.current = setInterval(() => {
        if (isTabActive() && !syncLockRef.current) {
          handleCheckMail(false, true); // Normal sync, auto-sync mode
        }
      }, POLLING_INTERVAL);
    };
    
    // Handle visibility change
    const handleVisibilityChange = () => {
      if (isTabActive()) {
        // Tab became active - do an immediate sync if last sync was >2 mins ago
        const timeSinceLastSync = lastSyncTime ? Date.now() - lastSyncTime.getTime() : Infinity;
        if (timeSinceLastSync > 120000) { // 2 minutes
          handleCheckMail(false, true);
        }
        startPolling();
      } else {
        // Tab became hidden - stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };
    
    // Initial setup
    if (isTabActive()) {
      startPolling();
    }
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [activeTab, lastSyncTime]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // EF-EMAIL-INBOX-AUTO-SYNC-V1: Calculate time since last sync for display
  const getSyncStatusText = () => {
    if (syncStatus === 'syncing') {
      return { text: 'Syncing...', color: 'text-blue-400' };
    }
    if (syncStatus === 'error') {
      return { text: 'Sync failed', color: 'text-red-400' };
    }
    if (!lastSyncTime) {
      return { text: 'Not synced', color: 'text-muted-foreground' };
    }
    
    const now = new Date();
    const diffMs = now - lastSyncTime;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffSecs < 30) {
      return { text: 'Synced just now', color: 'text-emerald-400' };
    }
    if (diffMins < 1) {
      return { text: `Synced ${diffSecs}s ago`, color: 'text-emerald-400' };
    }
    if (diffMins < 5) {
      return { text: `Synced ${diffMins}m ago`, color: 'text-emerald-400' };
    }
    if (diffMins < 10) {
      return { text: `Synced ${diffMins}m ago`, color: 'text-amber-400' };
    }
    return { text: `Synced ${diffMins}m ago`, color: 'text-amber-400' };
  };
  
  // Row click handler
  // EF-EMAIL-INBOX-STATE-MANAGEMENT-V1: Mark as read when opening email
  const handleRowClick = async (msg) => {
    // EF-CREW-DOCUMENT-INGESTION-FIX-V1: Load full message with attachments
    try {
      const response = await api.get(`/inbox/messages/${msg.id}`);
      const fullMessage = response.data;
      setSelectedMessage(fullMessage);
      
      // EF-EMAIL-INBOX-STATE-MANAGEMENT-V1: Mark as read (optimistic update)
      if (!msg.is_read) {
        // Optimistic update - update UI immediately
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
        
        // Then make API call
        try {
          await api.patch(`/inbox/messages/${msg.id}/read`, { is_read: true });
        } catch (err) {
          console.error('Failed to mark as read:', err);
          // Revert on failure (optional - could also just ignore)
        }
      }
      
      // Update status to reviewed if new
      if (msg.status === 'new') {
        // Correct endpoint: /inbox/messages/{id}/status?status=reviewed
        await api.patch(`/inbox/messages/${msg.id}/status?status=reviewed`);
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, status: 'reviewed' } : m));
      }
    } catch (error) {
      console.error("Error loading message:", error);
      // Fallback to basic message data
      setSelectedMessage(msg);
    }
  };
  
  // EF-EMAIL-INBOX-STATE-MANAGEMENT-V1: Toggle read/unread status
  const handleToggleRead = async (messageId, currentIsRead) => {
    const newIsRead = !currentIsRead;
    
    // Optimistic update
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: newIsRead } : m));
    if (selectedMessage?.id === messageId) {
      setSelectedMessage(prev => ({ ...prev, is_read: newIsRead }));
    }
    
    try {
      await api.patch(`/inbox/messages/${messageId}/read`, { is_read: newIsRead });
      toast.success(newIsRead ? 'Marked as read' : 'Marked as unread');
    } catch (error) {
      console.error('Failed to toggle read status:', error);
      // Revert on failure
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: currentIsRead } : m));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, is_read: currentIsRead }));
      }
      toast.error('Failed to update read status');
    }
  };
  
  // EF-EMAIL-INBOX-STATE-MANAGEMENT-V1: Mark all as read
  const handleMarkAllRead = async () => {
    try {
      const response = await api.post('/inbox/messages/mark-all-read');
      const count = response.data?.updated_count || 0;
      
      // Update local state
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      
      if (count > 0) {
        toast.success(`Marked ${count} message${count !== 1 ? 's' : ''} as read`);
      } else {
        toast.info('All messages are already read');
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };
  
  // EF-INBOX-UX-REFACTOR-V1: Handle flag color update
  const handleUpdateFlag = async (messageId, newFlagColor) => {
    // Optimistic update
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, flag_color: newFlagColor } : m));
    if (selectedMessage?.id === messageId) {
      setSelectedMessage(prev => ({ ...prev, flag_color: newFlagColor }));
    }
    
    try {
      await api.patch(`/inbox/messages/${messageId}/flag`, { flag_color: newFlagColor });
    } catch (error) {
      console.error('Failed to update flag:', error);
      // Revert on failure
      loadMessages(activeTab);
      toast.error('Failed to update flag');
    }
  };
  
  // EF-INBOX-UX-REFACTOR-V1: Cycle through flag colors
  const handleCycleFlag = (messageId, currentFlag) => {
    const currentIndex = FLAG_CYCLE_ORDER.indexOf(currentFlag || 'none');
    const nextIndex = (currentIndex + 1) % FLAG_CYCLE_ORDER.length;
    handleUpdateFlag(messageId, FLAG_CYCLE_ORDER[nextIndex]);
  };
  
  // EF-INBOX-UX-REFACTOR-V1: Font size handlers
  const handleIncreaseFontSize = () => {
    const newScale = Math.min(previewFontScale + 0.1, 1.5);
    setPreviewFontScale(newScale);
    localStorage.setItem('inbox-preview-font-scale', newScale.toString());
  };
  
  const handleDecreaseFontSize = () => {
    const newScale = Math.max(previewFontScale - 0.1, 0.7);
    setPreviewFontScale(newScale);
    localStorage.setItem('inbox-preview-font-scale', newScale.toString());
  };
  
  // Context menu handlers
  const handleContextMenu = (e, msg) => {
    e.preventDefault();
    setContextMenu({ show: true, x: e.clientX, y: e.clientY, message: msg });
  };
  
  useEffect(() => {
    const handleClickOutside = () => setContextMenu({ show: false, x: 0, y: 0, message: null });
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  
  // Preview Parse
  const handlePreviewParse = async (msg) => {
    setContextMenu({ show: false, x: 0, y: 0, message: null });
    setPreviewLoading(true);
    setShowPreviewDialog(true);
    try {
      const response = await api.post(`/inbox/messages/${msg.id}/preview-parse`);
      setPreviewData(response.data);
    } catch (error) {
      toast.error('Failed to parse email');
      setShowPreviewDialog(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Create Project
  const handleCreateProject = (msg) => {
    setContextMenu({ show: false, x: 0, y: 0, message: null });
    setSelectedMessage(msg);
    setShowCreateDialog(true);
  };
  
  const confirmCreateProject = async (forceOverride = false) => {
    if (!selectedMessage) return;
    try {
      const url = forceOverride 
        ? `/inbox/messages/${selectedMessage.id}/create-project?force=true`
        : `/inbox/messages/${selectedMessage.id}/create-project`;
      const response = await api.post(url);
      const data = response.data;
      
      if (!data.success && data.warning === 'duplicate_in_thread') {
        setShowCreateDialog(false);
        setThreadWarning({
          message: data.message,
          existingProjectId: data.existing_project_id,
          existingEmailSubject: data.existing_email_subject,
          sourceMessage: selectedMessage
        });
        return;
      }
      
      if (data.success) {
        // EF-INBOX-CREATE-PROJECT-PERSISTENCE-FIX-V1: Enhanced success message
        const shiftsCreated = data.shifts_created || 0;
        const projectName = data.project_name || 'Project';
        toast.success(`Created "${projectName}" with ${shiftsCreated} shifts`);
        setShowCreateDialog(false);
        setThreadWarning(null);
        await loadMessages(activeTab === 'all' ? null : activeTab);
        
        // Navigate to created project using new response format
        const projectId = data.project_id || data.result?.project_id;
        if (projectId) {
          navigate(`/projects/${projectId}`);
        }
      } else {
        // EF-INBOX-CREATE-PROJECT-PERSISTENCE-FIX-V1: Show stage-specific error
        const errorMsg = data.message || data.error || 'Failed to create project';
        const stage = data.stage ? ` (${data.stage})` : '';
        toast.error(`${errorMsg}${stage}`);
      }
    } catch (error) {
      toast.error('Failed to create project');
    }
  };
  
  const handleForceCreate = async () => {
    setThreadWarning(null);
    setSelectedMessage(threadWarning?.sourceMessage);
    await confirmCreateProject(true);
  };

  // EVENTSFLO-AI-PARSER-SELECTION-SCOPE-V1
  // Capture the user's current text selection IFF it lies inside the
  // currently rendered email preview body. If a selection is present
  // but lives outside the body (e.g. in the sidebar), we surface an
  // actionable toast and fall back to the full body — never silently.
  const handleOpenAIInterpreter = useCallback(() => {
    if (!selectedMessage) return;
    let selectedText = '';
    let selectionWarning = null;
    try {
      const sel = typeof window !== 'undefined' ? window.getSelection() : null;
      const raw = sel ? sel.toString() : '';
      const root = previewBodyRef.current;
      if (raw && raw.trim()) {
        let insideBody = false;
        if (root && sel.rangeCount > 0) {
          for (let i = 0; i < sel.rangeCount; i++) {
            const r = sel.getRangeAt(i);
            const node = r.commonAncestorContainer;
            if (root === node || root.contains(node)) {
              insideBody = true;
              break;
            }
          }
        }
        if (insideBody) {
          selectedText = raw.trim();
        } else {
          selectionWarning = 'Highlighted text was outside the email body — parsing the full email instead.';
        }
      }
    } catch (e) {
      selectionWarning = 'Could not read text selection — parsing the full email instead.';
    }
    if (selectionWarning) toast.warning(selectionWarning);
    setAiInterpreterMessage(selectedMessage);
    setAiInterpreterSelection(selectedText);
    setShowAIInterpreter(true);
  }, [selectedMessage]);

  // EVENTSFLO-PROJECT-INTAKE-PASTED-TEXT-INTO-EXISTING-PROJECT-FLOW-V1
  // Submit pasted text → creates an inbox_messages row → reload list →
  // auto-select the new message so the user can click the SAME existing
  // "Current Rules Draft" or "New AI Draft" buttons. Review-first by design.
  const handleSubmitPastedText = async () => {
    const text = (pasteText || '').trim();
    if (!text) {
      toast.info('Paste some text first');
      return;
    }
    setPasting(true);
    try {
      const res = await api.post('/inbox/messages/from-paste', {
        text,
        subject: pasteSubject?.trim() || undefined,
        source: pasteSource || 'manual_paste',
      });
      const saved = res.data?.message;
      if (!saved?.id) throw new Error('No message returned');
      toast.success('Pasted text added to Inbox — review and choose Current Rules Draft or New AI Draft');
      setShowPasteDialog(false);
      setPasteText('');
      setPasteSubject('');
      // Reload current tab and auto-select the new message so existing
      // Project Intake action buttons are immediately available.
      await loadMessages(activeTab === 'all' ? null : activeTab);
      setSelectedMessage(saved);
    } catch (err) {
      console.error('Paste-to-inbox failed:', err);
      toast.error(err?.response?.data?.detail || err.message || 'Could not add pasted text');
    } finally {
      setPasting(false);
    }
  };
  
  // Update status
  const updateStatus = async (messageId, newStatus) => {
    setContextMenu({ show: false, x: 0, y: 0, message: null });
    try {
      // EF-INBOX-FUNCTIONAL-RESTORE-V1: Use correct endpoint format
      await api.patch(`/inbox/messages/${messageId}/status?status=${newStatus}`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: newStatus } : m));
      // EVENTSFLO-INBOX-BATCH-HIDE-AND-PDF-VIEWER-CONTROLS-V1: clear preview when hidden
      if (newStatus === 'hidden' && selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      toast.success(`Message marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // EVENTSFLO-INBOX-HIDDEN-VIEW-AND-UNHIDE-V1: restore a hidden message.
  // Backend sets is_hidden=false and, if status=='hidden', restores it to 'new'.
  // Read state is preserved.
  const handleUnhide = useCallback(async (messageId) => {
    try {
      await api.patch(`/inbox/messages/${messageId}/unhide`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) setSelectedMessage(null);
      // Refresh stats so the visible / hidden counters update immediately.
      try {
        const page = await fetchPage(activeTab, 0);
        setServerStats(page.stats);
      } catch { /* non-fatal */ }
      toast.success('Message unhidden');
    } catch (error) {
      toast.error('Failed to unhide message');
    }
  }, [fetchPage, activeTab, selectedMessage]);

  // EVENTSFLO-INBOX-BATCH-HIDE-AND-PDF-VIEWER-CONTROLS-V1: batch hide selected emails
  const handleBatchHide = async () => {
    if (selectedIds.size === 0) return;
    setBatchHiding(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    try {
      // Fire all in parallel; tolerate individual failures.
      const results = await Promise.allSettled(
        ids.map(id => api.patch(`/inbox/messages/${id}/status?status=hidden`))
      );
      results.forEach(r => { if (r.status === 'fulfilled') ok++; else fail++; });
      // Optimistic UI update — flag every successfully (or assumed) hidden id.
      setMessages(prev =>
        prev.map(m => (selectedIds.has(m.id) ? { ...m, status: 'hidden' } : m))
      );
      if (selectedMessage && selectedIds.has(selectedMessage.id)) {
        setSelectedMessage(null);
      }
      clearSelection();
      if (fail === 0) {
        toast.success(`Hid ${ok} email${ok === 1 ? '' : 's'}`);
      } else {
        toast.warning(`Hid ${ok} email(s); ${fail} failed`);
      }
    } catch {
      toast.error('Batch hide failed');
    } finally {
      setBatchHiding(false);
    }
  };
  
  // EF-EMAIL-INBOX-STABILISATION-V1: Delete message handler
  const handleDeleteMessage = async () => {
    if (!deleteConfirm.message) return;
    
    const messageId = deleteConfirm.message.id;
    setDeleting(true);
    
    try {
      await api.delete(`/inbox/messages/${messageId}`);
      
      // Remove from local state
      setMessages(prev => prev.filter(m => m.id !== messageId));
      
      // Clear selection if deleted message was selected
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      
      toast.success('Message deleted successfully');
      setDeleteConfirm({ show: false, message: null });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    } finally {
      setDeleting(false);
    }
  };

  // EVENTSFLO-INBOX-DOWNLOAD-EMAIL-AS-EML-V1
  // Request the backend-assembled .eml for the currently selected email and
  // trigger a browser download. Does NOT mutate the email's state (read,
  // status, archive, etc.). Uses blob + temporary <a> click so the
  // Authorization header is preserved and the browser still respects the
  // Content-Disposition filename returned by the API.
  const handleDownloadEml = async (msg) => {
    if (!msg?.id) {
      toast.error('Download .eml failed: no email selected');
      return;
    }
    let toastId;
    try {
      toastId = toast.loading('Preparing .eml…');
      const response = await api.get(`/inbox/messages/${msg.id}/eml`, {
        responseType: 'blob',
      });

      // Parse filename from Content-Disposition (server returns the safe name)
      let filename = '';
      const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
      if (disposition) {
        const match = /filename="?([^"]+)"?/i.exec(disposition);
        if (match && match[1]) filename = match[1];
      }
      if (!filename) {
        const subject = (msg.subject || 'inbox-email').replace(/[\\/:"*?<>|]+/g, '_').slice(0, 60);
        filename = `${subject || 'inbox-email'}.eml`;
      }

      // axios already delivers response.data as a Blob with the right MIME
      // (from server's Content-Type: message/rfc822). Re-wrapping is only
      // needed if the upstream blob type is wrong.
      let blob = response.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([response.data], { type: 'message/rfc822' });
      }
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      try {
        a.click();
      } catch (clickErr) {
        // Some browsers / dev overlays surface anchor.click() races as a
        // generic "Script error.". Fall back to window-level navigation so
        // the user still gets the file.
        console.warn('Download .eml anchor.click fallback:', clickErr);
        window.location.href = blobUrl;
      }
      // Defer cleanup so the browser can dispatch the download before the
      // blob URL is revoked. Synchronous revoke after click() can race with
      // the download pipeline and surface as a generic cross-origin
      // "Script error." in the CRA dev overlay.
      setTimeout(() => {
        try {
          if (a.parentNode) a.parentNode.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        } catch (cleanupErr) {
          // Cleanup-only failure must never bubble; the download has
          // already been dispatched at this point.
          console.warn('Download .eml cleanup warning:', cleanupErr);
        }
      }, 1000);

      toast.success('Email downloaded', { id: toastId });
    } catch (error) {
      console.error('Download .eml error:', error);
      // Blob errors carry the JSON body inside response.data (a Blob), parse it
      let detail = '';
      try {
        const blob = error?.response?.data;
        if (blob && typeof blob.text === 'function') {
          const text = await blob.text();
          try {
            const parsed = JSON.parse(text);
            detail = parsed?.detail || text;
          } catch {
            detail = text;
          }
        }
      } catch { /* noop */ }
      if (!detail) detail = error?.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Download .eml failed: ${detail}`, { id: toastId });
    }
  };

  // Filter messages
  // EF-INBOX-UX-REFACTOR-V1: Enhanced filtering and sorting
  // EVENTSFLO-INBOX-HIDDEN-VIEW-AND-UNHIDE-V1: backend already filters by
  // is_hidden for the active tab; client-side guard is a defence-in-depth.
  const filteredMessages = messages
    .filter(msg => {
      if (activeTab === 'hidden') {
        if (!(msg.is_hidden === true || msg.status === 'hidden')) return false;
      } else {
        if (msg.is_hidden === true || msg.status === 'hidden') return false;
        if (activeTab !== 'all' && msg.status !== activeTab) return false;
      }

      // Flag filter
      if (filterFlag && msg.flag_color !== filterFlag) return false;
      
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          msg.subject?.toLowerCase().includes(term) ||
          msg.sender?.toLowerCase().includes(term) ||
          msg.detected_prefix?.toLowerCase().includes(term)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'flag') {
        // Sort by flag color priority (red > green > blue > yellow > grey > none)
        const flagPriority = { red: 0, green: 1, blue: 2, yellow: 3, grey: 4, none: 5, black: 5 };
        const aPriority = flagPriority[a.flag_color || 'none'] ?? 5;
        const bPriority = flagPriority[b.flag_color || 'none'] ?? 5;
        if (aPriority !== bPriority) {
          return sortOrder === 'asc' ? bPriority - aPriority : aPriority - bPriority;
        }
        // Within same flag, sort by date
        const aDate = new Date(a.received_at);
        const bDate = new Date(b.received_at);
        return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
      }
      // Default: sort by date
      const aDate = new Date(a.received_at);
      const bDate = new Date(b.received_at);
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    });
  
  // Stats
  // EVENTSFLO-INBOX-PAGINATION-AND-HIDDEN-FILTER-V1:
  //   - Prefer authoritative server stats (computed over the full visible set
  //     with is_hidden != true), falling back to loaded-page counts only when
  //     the server response did not include them.
  //   - Flag counts remain client-side (loaded page only) since the backend
  //     does not expose per-flag counts yet — they update as the user scrolls.
  const stats = {
    total: serverStats?.total ?? messages.length,
    new: serverStats?.new ?? messages.filter(m => m.status === 'new').length,
    reviewed: serverStats?.reviewed ?? messages.filter(m => m.status === 'reviewed').length,
    processed: serverStats?.processed ?? messages.filter(m => m.status === 'processed').length,
    unread: serverStats?.unread ?? messages.filter(m => !m.is_read).length,
    flagged: {
      red: messages.filter(m => m.flag_color === 'red').length,
      green: messages.filter(m => m.flag_color === 'green').length,
      blue: messages.filter(m => m.flag_color === 'blue').length,
      yellow: messages.filter(m => m.flag_color === 'yellow').length,
    }
  };
  
  // Table columns - EF-INBOX-UX-REFACTOR-V1: Added flag column
  const columns = [
    { key: 'flag', label: '', sortable: false }, // Flag icon column
    { key: 'received_at', label: 'Date', sortable: true, sortKey: 'received_at' },
    { key: 'sender', label: 'From', sortable: true },
    { key: 'subject', label: 'Subject', sortable: true },
    { key: 'thread', label: 'Thread', sortable: false },
    { key: 'type', label: 'Type', sortable: false },
    { key: 'status', label: 'Status', sortable: true },
  ];

  if (loading) {
    return <LoadingSpinner message="Loading inbox..." />;
  }

  return (
    <div ref={containerRef} className="min-h-screen">
      {/* EF-GLOBAL-PAGE-ZOOM-FIX-V1: Use centralized PageZoomContainer */}
      <PageZoomContainer scale={scale}>
      <PageHeader
        title="Inbox"
        subtitle={`Operations Intake Hub • ${stats.unread > 0 ? `${stats.unread} unread • ` : ''}${stats.new} new • ${stats.total} total`}
        actions={
          <div className="flex items-center gap-3">
            {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
            <GuideButton testId="inbox-guide-btn" />
            <ZoomControl
              currentLabel={currentZoom.label}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              canZoomIn={canZoomIn}
              canZoomOut={canZoomOut}
              wrapText={wrapText}
              onToggleWrap={toggleWrap}
            />
            {/* EVENTSFLO-INBOX-INTAKE-STRUCTURE-AND-HELP-V1 */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setInboxHelpOpen(true)}
              data-testid="inbox-help-btn"
            >
              <HelpCircle className="w-4 h-4 mr-1" />
              Help
            </Button>
            {/* EF-EMAIL-INBOX-STATE-MANAGEMENT-V1: Mark All Read button */}
            {stats.unread > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllRead}
                data-testid="mark-all-read-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark All Read
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings/email')}
              data-testid="inbox-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            {/* EVENTSFLO-PROJECT-INTAKE-PASTED-TEXT-INTO-EXISTING-PROJECT-FLOW-V1 */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPasteSubject('');
                setPasteText('');
                setShowPasteDialog(true);
              }}
              className="border-primary/40 text-primary hover:bg-primary/10"
              title="Paste a project request from WhatsApp / SMS / email / notes"
              data-testid="inbox-paste-text-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Paste Text
            </Button>
            <div className="flex">
              <Button
                onClick={() => handleCheckMail(false)}
                disabled={fetching || syncLockRef.current}
                className="bg-primary text-primary-foreground rounded-r-none"
                data-testid="check-mail-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
                Check Mail
              </Button>
              <Button
                onClick={() => handleCheckMail(true)}
                disabled={fetching || syncLockRef.current}
                variant="outline"
                className="rounded-l-none border-l-0 px-2"
                title="Force full sync - re-check all emails"
                data-testid="force-sync-btn"
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>
            {/* EF-EMAIL-INBOX-AUTO-SYNC-V1: Sync status indicator */}
            <div className="flex items-center gap-2 text-xs">
              <span className={`flex items-center gap-1 ${getSyncStatusText().color}`}>
                {syncStatus === 'syncing' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : syncStatus === 'error' ? (
                  <AlertTriangle className="w-3 h-3" />
                ) : (
                  <span className={`w-2 h-2 rounded-full ${
                    syncStatus === 'success' && lastSyncTime && (Date.now() - lastSyncTime.getTime()) < 300000
                      ? 'bg-emerald-400'
                      : 'bg-amber-400'
                  }`} />
                )}
                {getSyncStatusText().text}
              </span>
            </div>
          </div>
        }
      />
      
      {/* View Mode Toggle: Messages vs Attachments */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex gap-1 bg-card border border-white/5 rounded-sm p-1">
          <button
            onClick={() => setViewMode('messages')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
              viewMode === 'messages'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-surface-elevated'
            }`}
            data-testid="view-mode-messages"
          >
            <Mail className="w-4 h-4" />
            Messages
          </button>
          <button
            onClick={() => setViewMode('attachments')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
              viewMode === 'attachments'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-surface-elevated'
            }`}
            data-testid="view-mode-attachments"
          >
            <Paperclip className="w-4 h-4" />
            Attachments
          </button>
        </div>
      </div>

      {/* Attachments View */}
      {viewMode === 'attachments' ? (
        <EmailAttachmentManager />
      ) : (
        <>
      {/* EF-INBOX-V1-STEP2: First-time user guidance banner */}
      {!hasEverFetched && messages.length === 0 && !fetching && (
        <div className="mb-4 p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-start gap-3">
          <Info className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-sm">Getting Started with Inbox</p>
            <p className="text-sm text-muted-foreground mt-1">
              Click the <strong>"Check Mail"</strong> button above to fetch emails from your configured email account.
              Emails will be automatically categorized and you can process them into projects and shifts.
            </p>
          </div>
        </div>
      )}

      {/* Filter Tabs & Search */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 bg-card border border-white/5 rounded-sm p-1">
          {['all', 'new', 'reviewed', 'processed', 'hidden'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-sm transition-colors ${
                activeTab === tab 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-surface-elevated'
              }`}
              data-testid={`inbox-tab-${tab}`}
            >
              {tab} {tab !== 'all' && `(${stats[tab]})`}
            </button>
          ))}
        </div>
        
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search inbox..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-card border-white/5"
            data-testid="inbox-search"
          />
        </div>
      </div>

      {/* Main Content - Two Column Layout with Fixed Preview */}
      {/* EF-INBOX-PREVIEW-ANCHOR-FIX-V1: Ensure preview is always visible in viewport */}
      {/* EF-INBOX-UX-FINAL-POLISH-V1: Added right padding for breathing room */}
      <div className="flex h-[calc(100vh-180px)] gap-0 overflow-hidden pr-4">
        {/* Email List - Constrained width to ensure preview visibility */}
        <div 
          className="bg-card border border-white/5 rounded-sm flex-1 min-w-0 relative flex flex-col"
          style={{ 
            maxWidth: selectedMessage ? 'calc(100% - 440px)' : '100%',
            transition: 'max-width 0.2s ease-in-out'
          }}
        >
          {/* EF-INBOX-V1-STEP2: Loading overlay during fetch */}
          {fetching && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 p-6">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                <div className="text-center">
                  <p className="font-medium">Fetching emails from server...</p>
                  <p className="text-sm text-muted-foreground">This may take a few seconds</p>
                </div>
              </div>
            </div>
          )}
          
          {/* EF-INBOX-V1-STEP2: Context-aware empty states */}
          {filteredMessages.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-16 px-4">
              <InboxIcon className="w-12 h-12 text-muted-foreground/50 mb-4" />
              
              {/* Different messages based on context */}
              {messages.length === 0 && !hasEverFetched ? (
                // First-time user - never checked mail
                <>
                  <h3 className="text-lg font-semibold mb-2">Welcome to your Inbox</h3>
                  <p className="text-muted-foreground text-center mb-4 max-w-md">
                    Click <strong>"Check Mail"</strong> to fetch emails from your configured email account.
                  </p>
                  <Button onClick={() => handleCheckMail(false)} disabled={fetching || syncLockRef.current} size="lg">
                    <RefreshCw className={`w-4 h-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
                    Check Mail
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    Need to configure email? Go to{' '}
                    <button 
                      onClick={() => navigate('/settings/email')} 
                      className="text-primary hover:underline"
                    >
                      Email Settings
                    </button>
                  </p>
                </>
              ) : messages.length === 0 ? (
                // Has checked before but inbox is empty
                <>
                  <h3 className="text-lg font-semibold mb-2">No emails yet</h3>
                  <p className="text-muted-foreground text-center mb-4 max-w-md">
                    Your inbox is empty. Click "Check Mail" to fetch new emails.
                  </p>
                  <Button onClick={() => handleCheckMail(false)} disabled={fetching || syncLockRef.current}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
                    Check Mail
                  </Button>
                </>
              ) : activeTab !== 'all' ? (
                // Has messages but filter shows none
                <>
                  <h3 className="text-lg font-semibold mb-2">
                    No {activeTab} emails
                  </h3>
                  <p className="text-muted-foreground text-center mb-4 max-w-md">
                    {activeTab === 'new' && "All emails have been reviewed. Great job staying on top of your inbox!"}
                    {activeTab === 'reviewed' && "No emails are currently marked as reviewed."}
                    {activeTab === 'processed' && "No emails have been processed yet."}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setActiveTab('all')}>
                      View All Emails
                    </Button>
                    <Button onClick={handleCheckMail} disabled={fetching}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
                      Check Mail
                    </Button>
                  </div>
                </>
              ) : searchTerm ? (
                // Search returned no results
                <>
                  <h3 className="text-lg font-semibold mb-2">No results found</h3>
                  <p className="text-muted-foreground text-center mb-4 max-w-md">
                    No emails match "<strong>{searchTerm}</strong>". Try a different search term.
                  </p>
                  <Button variant="outline" onClick={() => setSearchTerm('')}>
                    Clear Search
                  </Button>
                </>
              ) : (
                // Generic empty state
                <>
                  <h3 className="text-lg font-semibold mb-2">Inbox is empty</h3>
                  <p className="text-muted-foreground text-center mb-4">
                    No messages to display. Check mail to fetch new emails.
                  </p>
                  <Button onClick={() => handleCheckMail(false)} disabled={fetching || syncLockRef.current}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
                    Check Mail
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div 
              className={`flex-1 overflow-y-auto ${!wrapText ? '[&_td]:whitespace-nowrap [&_td]:overflow-hidden [&_td]:text-ellipsis' : ''}`}
            >
              {/* EF-INBOX-UX-REFACTOR-V1: Sorting and filtering controls */}
              {/* EF-INBOX-UX-FINAL-POLISH-V1: Fixed sticky header click blocking */}
              <div className="bg-card px-4 py-2 border-b border-white/5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {/* Sort dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
                        <ArrowUpDown className="w-3 h-3" />
                        Sort: {sortBy === 'date' ? 'Date' : 'Flag'}
                        {sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => { setSortBy('date'); setSortOrder('desc'); }}>
                        <ArrowDown className="w-3 h-3 mr-2" /> Newest First
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setSortBy('date'); setSortOrder('asc'); }}>
                        <ArrowUp className="w-3 h-3 mr-2" /> Oldest First
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { setSortBy('flag'); setSortOrder('desc'); }}>
                        <Flag className="w-3 h-3 mr-2" /> By Flag (Priority)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Flag filter */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
                        <Flag className={`w-3 h-3 ${filterFlag ? FLAG_COLORS[filterFlag]?.color : ''}`} />
                        {filterFlag ? FLAG_COLORS[filterFlag]?.label : 'All Flags'}
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => setFilterFlag(null)}>
                        All Flags
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {Object.entries(FLAG_COLORS).filter(([k]) => k !== 'none' && k !== 'black').map(([color, config]) => (
                        <DropdownMenuItem key={color} onClick={() => setFilterFlag(color)}>
                          <Flag className={`w-3 h-3 mr-2 ${config.color}`} />
                          {config.label}
                          {stats.flagged[color] > 0 && (
                            <Badge variant="outline" className="ml-auto text-[9px] h-4">
                              {stats.flagged[color]}
                            </Badge>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <span className="text-xs text-muted-foreground" data-testid="inbox-row-count">
                  Showing {filteredMessages.length} of {stats.total} email{stats.total !== 1 ? 's' : ''}
                </span>
              </div>

              {/* EVENTSFLO-INBOX-BATCH-HIDE-AND-PDF-VIEWER-CONTROLS-V1: batch action bar */}
              {selectedIds.size > 0 && (
                <div
                  className="flex items-center justify-between gap-2 px-3 py-2 mb-2 rounded border border-amber-500/30 bg-amber-500/10"
                  data-testid="inbox-batch-action-bar"
                >
                  <span className="text-xs text-amber-200">
                    <strong>{selectedIds.size}</strong> selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={clearSelection}
                      data-testid="inbox-batch-clear"
                    >
                      Clear
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs text-amber-300 border-amber-500/40 hover:bg-amber-500/20"
                      onClick={handleBatchHide}
                      disabled={batchHiding}
                      data-testid="inbox-batch-hide"
                    >
                      <EyeOff className="w-3 h-3 mr-1" />
                      {batchHiding ? 'Hiding…' : `Hide selected (${selectedIds.size})`}
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  {/* EF-INBOX-UX-FINAL-POLISH-V1: Removed sticky from thead to prevent click blocking */}
                  <thead className="bg-card">
                    <tr>
                      {/* EVENTSFLO-INBOX-BATCH-HIDE-AND-PDF-VIEWER-CONTROLS-V1: select column */}
                      <th className="px-2 py-3 text-left w-8">
                        <input
                          type="checkbox"
                          aria-label="Select all visible emails"
                          checked={
                            filteredMessages.length > 0 &&
                            filteredMessages.every((m) => selectedIds.has(m.id))
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds(new Set(filteredMessages.map((m) => m.id)));
                            } else {
                              clearSelection();
                            }
                          }}
                          data-testid="inbox-select-all"
                        />
                      </th>
                      {columns.map((col) => (
                        <th key={col.key} className={`px-4 py-3 text-left ${col.key === 'flag' ? 'w-8' : ''}`}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMessages.map((msg) => {
                      const isSelected = selectedMessage?.id === msg.id;
                      return (
                      <tr
                        key={msg.id}
                        className={`cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary/15 shadow-[inset_4px_0_0_0_hsl(var(--primary))] font-medium'
                            : 'hover:bg-surface-elevated/50'
                        }`}
                        onClick={() => handleRowClick(msg)}
                        onContextMenu={(e) => handleContextMenu(e, msg)}
                        aria-selected={isSelected ? 'true' : 'false'}
                        data-testid={isSelected ? 'inbox-message-row-selected' : `inbox-row-${msg.id}`}
                      >
                        {/* EVENTSFLO-INBOX-BATCH-HIDE-AND-PDF-VIEWER-CONTROLS-V1: select cell */}
                        <td className="px-2 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            aria-label={`Select email ${msg.subject || msg.id}`}
                            checked={selectedIds.has(msg.id)}
                            onChange={() => toggleSelected(msg.id)}
                            data-testid={`inbox-select-${msg.id}`}
                          />
                        </td>
                        {/* EF-INBOX-UX-REFACTOR-V1: Flag column */}
                        <td className="px-2 py-3 w-8">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCycleFlag(msg.id, msg.flag_color);
                            }}
                            className={`p-1 rounded ${FLAG_COLORS[msg.flag_color || 'none']?.bgHover} transition-colors`}
                            title={`Flag: ${FLAG_COLORS[msg.flag_color || 'none']?.label}`}
                            data-testid={`flag-btn-${msg.id}`}
                          >
                            {msg.flag_color && msg.flag_color !== 'none' ? (
                              <Flag className={`w-4 h-4 ${FLAG_COLORS[msg.flag_color]?.color}`} fill="currentColor" />
                            ) : (
                              <Flag className="w-4 h-4 text-zinc-600" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">{formatDate(msg.received_at)}</td>
                        <td className="px-4 py-3 max-w-[200px] truncate">{msg.sender}</td>
                        {/* EF-EMAIL-INBOX-STATE-MANAGEMENT-V1: Bold subject + indicator dot for unread */}
                        <td className={`px-4 py-3 max-w-[300px] truncate ${!msg.is_read ? 'font-semibold' : ''}`}>
                          <div className="flex items-center gap-2">
                            {!msg.is_read && (
                              <Circle className="w-2 h-2 fill-primary text-primary flex-shrink-0" data-testid="unread-indicator" />
                            )}
                            <span className="truncate">{msg.subject}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {msg.thread_count > 1 ? (
                            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                              <MessageSquare className="w-3 h-3 mr-1" />
                              {msg.thread_count}
                            </Badge>
                          ) : msg.thread_id || msg.in_reply_to ? (
                            <Link2 className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <TypeBadge prefix={msg.detected_prefix} reference={msg.detected_reference} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <InboxStatusBadge status={msg.status} />
                            {msg.project_created && (
                              <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px]">
                                PRJ
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* EVENTSFLO-INBOX-PAGINATION-AND-HIDDEN-FILTER-V1: Load more */}
              {hasMore && (
                <div className="flex items-center justify-center py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="inbox-load-more-btn"
                  >
                    {loadingMore
                      ? 'Loading…'
                      : `Load more (${Math.max(stats.total - messages.length, 0)} remaining)`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Resizable Divider - Always visible */}
        <div
          onMouseDown={selectedMessage ? handleMouseDown : undefined}
          className={`w-1.5 bg-border ${selectedMessage ? 'hover:bg-primary/50 cursor-col-resize' : ''} flex items-center justify-center transition-colors ${
            isDragging ? 'bg-primary' : ''
          }`}
          data-testid="preview-divider"
        >
          <GripVertical className="w-3 h-3 text-muted-foreground" />
        </div>
        
        {/* Preview Panel - Always visible, shows placeholder when no email selected */}
        <div 
          className="bg-card border border-white/5 rounded-sm flex flex-col h-full"
          style={{ width: `${previewWidth}px`, minWidth: '400px', flexShrink: 0 }}
          data-testid="preview-panel"
        >
          {selectedMessage ? (
            <>
            {/* EF-INBOX-UX-REFACTOR-V1: Fixed Preview Header with ALL Actions */}
            <div className="px-3 py-2 border-b border-white/5 flex-shrink-0">
              {/* Top row: Email info (left) + Primary Actions (right) */}
              <div className="flex items-start justify-between gap-2">
                {/* Left: Flag + Email info */}
                <div className="min-w-0 flex-1 flex items-start gap-2 overflow-hidden">
                  {/* Flag button dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className={`p-1 rounded ${FLAG_COLORS[selectedMessage.flag_color || 'none']?.bgHover} transition-colors flex-shrink-0`}
                        data-testid="preview-flag-btn"
                      >
                        {selectedMessage.flag_color && selectedMessage.flag_color !== 'none' ? (
                          <Flag className={`w-4 h-4 ${FLAG_COLORS[selectedMessage.flag_color]?.color}`} fill="currentColor" />
                        ) : (
                          <Flag className="w-4 h-4 text-zinc-500" />
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {Object.entries(FLAG_COLORS).map(([color, config]) => (
                        <DropdownMenuItem 
                          key={color} 
                          onClick={() => handleUpdateFlag(selectedMessage.id, color)}
                        >
                          <Flag className={`w-3 h-3 mr-2 ${config.color}`} fill={color !== 'none' ? 'currentColor' : 'none'} />
                          {config.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <div className="min-w-0 overflow-hidden">
                    <h3 className="font-medium text-sm leading-tight truncate" title={selectedMessage.subject}>
                      {selectedMessage.subject}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate mt-0.5" title={selectedMessage.sender}>
                      {selectedMessage.sender}
                    </p>
                  </div>
                </div>
                
                {/* Right: Close button only */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 flex-shrink-0"
                  onClick={() => setSelectedMessage(null)}
                  data-testid="preview-close-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* EVENTSFLO-INBOX-INTAKE-STRUCTURE-AND-HELP-V1: action buttons grouped by purpose
                  - Project Intake: Current Rules Draft (Create) + New AI Draft (review-only)
                  - Crew Intake: per-attachment "Onboard" buttons live in the attachments section below
                  - General: Parse / Done / Hide / Dismiss / Delete */}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 overflow-x-auto">
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 shrink-0 select-none">
                  Project Intake
                </span>
                <Button
                  size="sm"
                  className="h-7 text-xs bg-primary text-primary-foreground flex-shrink-0"
                  onClick={() => handleCreateProject(selectedMessage)}
                  data-testid="preview-create-project-btn"
                  title="Create using the established rules-based path"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Current Rules Draft
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10 flex-shrink-0"
                  onClick={handleOpenAIInterpreter}
                  data-testid="preview-ai-interpreter-btn"
                  title="New AI draft (review-only). Tip: highlight a portion of the email body first to parse only that section."
                >
                  <Bot className="w-3 h-3 mr-1" />
                  New AI Draft
                </Button>

                <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 shrink-0 select-none ml-1">
                  Crew Intake
                </span>
                <span className="text-[10px] text-muted-foreground italic shrink-0">
                  see attachments below for "Onboard"
                </span>

                <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 shrink-0 select-none ml-1">
                  General
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs flex-shrink-0"
                  onClick={() => handlePreviewParse(selectedMessage)}
                  data-testid="preview-parse-email-btn"
                  title="Quick parse preview"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Parse
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-emerald-500 flex-shrink-0"
                  onClick={() => updateStatus(selectedMessage.id, 'processed')}
                  title="Mark email as handled"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Done
                </Button>
                {/* EVENTSFLO-INBOX-DOWNLOAD-EMAIL-AS-EML-V1 */}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs flex-shrink-0"
                  onClick={() => handleDownloadEml(selectedMessage)}
                  data-testid="download-eml-btn"
                  title="Download this email (with attachments) as a local .eml file"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download .eml
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-amber-400 border-amber-500/30 hover:bg-amber-500/10 flex-shrink-0"
                  onClick={() => updateStatus(selectedMessage.id, 'hidden')}
                  data-testid="preview-hide-btn"
                  title="Hide as spam / irrelevant (kept in DB, removed from active queues)"
                >
                  <EyeOff className="w-3 h-3 mr-1" />
                  Hide
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground flex-shrink-0"
                  onClick={() => updateStatus(selectedMessage.id, 'dismissed')}
                  title="Reviewed but not actioned"
                >
                  <X className="w-3 h-3 mr-1" />
                  Dismiss
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-red-500 hover:text-red-400 hover:bg-red-500/10 border-red-500/30 flex-shrink-0"
                  onClick={() => setDeleteConfirm({ show: true, message: selectedMessage })}
                  title="Permanently delete email and attachments"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </Button>
              </div>
              
              {/* Status row with date and font controls */}
              <div className="flex items-center gap-2 mt-2 text-xs">
                <span className="text-muted-foreground">{formatDate(selectedMessage.received_at)}</span>
                {selectedMessage.thread_id && (
                  <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-[9px] h-4">
                    <MessageSquare className="w-2.5 h-2.5 mr-0.5" />
                    Thread
                  </Badge>
                )}
                <InboxStatusBadge status={selectedMessage.status} />
                
                {/* Spacer */}
                <div className="flex-1" />
                
                {/* Read/Unread toggle */}
                <button
                  onClick={() => handleToggleRead(selectedMessage.id, selectedMessage.is_read)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title={selectedMessage.is_read ? 'Mark as unread' : 'Mark as read'}
                >
                  {selectedMessage.is_read ? <Mail className="w-3.5 h-3.5" /> : <MailOpen className="w-3.5 h-3.5" />}
                </button>
                
                {/* Font size controls */}
                <div className="flex items-center gap-0.5 border border-white/10 rounded">
                  <button
                    onClick={handleDecreaseFontSize}
                    className="px-1.5 py-0.5 text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors rounded-l"
                    title="Decrease font size"
                    disabled={previewFontScale <= 0.7}
                    data-testid="font-decrease-btn"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="px-1 text-[9px] text-muted-foreground border-x border-white/10">
                    {Math.round(previewFontScale * 100)}%
                  </span>
                  <button
                    onClick={handleIncreaseFontSize}
                    className="px-1.5 py-0.5 text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors rounded-r"
                    title="Increase font size"
                    disabled={previewFontScale >= 1.5}
                    data-testid="font-increase-btn"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Scrollable Content Area - EF-INBOX-UX-REFACTOR-V1: No footer, font scaling */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Email Body with font scaling */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Body</label>
                <div 
                  ref={previewBodyRef}
                  data-testid="preview-body"
                  className={`bg-surface-elevated rounded p-3 overflow-y-auto ${wrapText ? 'whitespace-pre-wrap break-words' : 'whitespace-pre-wrap'}`}
                  style={{ fontSize: `${previewFontScale}rem`, lineHeight: 1.6 }}
                >
                  {selectedMessage.body_raw || selectedMessage.body_preview || 'No content'}
                </div>
              </div>
              
              {/* EF-CREW-DOCUMENT-INGESTION-FIX-V1: Attachments under email body */}
              {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Paperclip className="w-3 h-3" />
                      Attachments ({selectedMessage.attachments.length})
                    </label>
                    {/* EVENTSFLO-INBOX-ATTACHMENT-TO-AI-ONBOARD-CREW-V1: bulk send to onboarding */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[10px] border-primary/30 text-primary hover:bg-primary/10"
                      onClick={() => {
                        const docs = (selectedMessage.attachments || [])
                          .filter((a) => {
                            const m = (a.mime_type || "").toLowerCase();
                            const n = (a.original_filename || "").toLowerCase();
                            return (
                              m === "application/pdf" ||
                              m.startsWith("image/") ||
                              n.endsWith(".pdf") || n.endsWith(".jpg") ||
                              n.endsWith(".jpeg") || n.endsWith(".png") || n.endsWith(".webp") ||
                              // EVENTSFLO-CREW-ONBOARDING-TXT-RTF-TEXT-EXTRACTION-V1
                              m === "text/plain" || m === "text/rtf" || m === "application/rtf" ||
                              n.endsWith(".txt") || n.endsWith(".rtf")
                            );
                          })
                          .map((a) => ({
                            attachment_id: a.id,
                            message_id: selectedMessage.id,
                            original_filename: a.original_filename,
                            mime_type: a.mime_type || "application/octet-stream",
                          }));
                        if (docs.length === 0) {
                          toast.error("No supported attachments (PDF/JPG/PNG/WEBP/TXT/RTF) on this email.");
                          return;
                        }
                        // EVENTSFLO-CREW-INBOX-ONBOARDING-DOCUMENT-PERSISTENCE-HEALTH-EMAIL-FALLBACK-V1
                        // Carry the email sender through so onboarding can use
                        // it as a fallback if no email is extracted.
                        const inboxSender = (() => {
                          const raw = String(selectedMessage.sender || "").trim();
                          // sender often comes as "Name <email@host>" — strip name
                          const m = raw.match(/<([^>]+)>/);
                          return (m ? m[1] : raw).toLowerCase();
                        })();
                        navigate("/ai-crew-onboarding", {
                          state: {
                            inboxAttachments: docs,
                            inboxSenderEmail: inboxSender || null,
                            inboxMessageId: selectedMessage.id,
                          },
                        });
                      }}
                      data-testid="onboard-all-attachments-btn"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Onboard Crew ({selectedMessage.attachments.length})
                    </Button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {selectedMessage.attachments.map((att) => (
                      <div 
                        key={att.id}
                        className="flex items-center justify-between p-2 bg-surface-elevated rounded border border-white/5 hover:border-primary/30 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {/* EVENTSFLO-FIX-INBOX-PNG-PREVIEW-V1: render real inline thumbnail
                              for image attachments (PNG, JPEG, WEBP, etc.) instead of a
                              generic icon. Browser-native rendering covers PNG correctly. */}
                          {att.mime_type?.startsWith('image/') ? (
                            <img
                              src={withInboxAttachmentToken(`${API_URL}/api/inbox/attachments/${att.id}/file`)}
                              alt={att.original_filename}
                              className="w-9 h-9 rounded object-cover bg-zinc-800/60 border border-white/10 shrink-0"
                              loading="lazy"
                              draggable={false}
                              onError={(e) => {
                                // Fallback to icon if the image fails to load
                                const img = e.currentTarget;
                                img.style.display = 'none';
                                const fallback = img.nextElementSibling;
                                if (fallback) fallback.style.display = 'inline-block';
                              }}
                              data-testid={`attachment-thumb-${att.id}`}
                            />
                          ) : null}
                          {att.mime_type?.startsWith('image/') ? (
                            <Image
                              className="w-4 h-4 text-purple-400 shrink-0"
                              style={{ display: 'none' }}
                            />
                          ) : att.mime_type?.includes('pdf') ? (
                            <FileText className="w-4 h-4 text-red-400 shrink-0" />
                          ) : (
                            <File className="w-4 h-4 text-zinc-400 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm truncate" title={att.original_filename}>{att.original_filename}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-muted-foreground">
                                {(att.file_size_bytes / 1024).toFixed(1)} KB
                              </span>
                              {att.detected_document_type && att.detected_document_type !== 'unknown' && (
                                <Badge variant="outline" className="text-[9px] h-4 px-1 capitalize">
                                  {att.detected_document_type.replace('_', ' ')}
                                </Badge>
                              )}
                              {att.ocr_extracted_data && Object.keys(att.ocr_extracted_data).length > 0 && (
                                <Badge variant="outline" className="text-[9px] h-4 px-1 text-emerald-400 border-emerald-500/30">
                                  OCR
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-primary"
                            onClick={() => {
                              setSelectedAttachmentForReview(att);
                              setAllMessageAttachments(selectedMessage.attachments);
                              setWorkspaceOpen(true);
                            }}
                            data-testid={`review-inline-${att.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Review
                          </Button>
                          {/* EVENTSFLO-INBOX-ATTACHMENT-TO-AI-ONBOARD-CREW-V1: per-attachment send to onboarding */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-amber-300"
                            onClick={() => {
                              const m = (att.mime_type || "").toLowerCase();
                              const n = (att.original_filename || "").toLowerCase();
                              const isSupported =
                                m === "application/pdf" ||
                                m.startsWith("image/") ||
                                n.endsWith(".pdf") || n.endsWith(".jpg") ||
                                n.endsWith(".jpeg") || n.endsWith(".png") || n.endsWith(".webp") ||
                                // EVENTSFLO-CREW-ONBOARDING-TXT-RTF-TEXT-EXTRACTION-V1
                                m === "text/plain" || m === "text/rtf" || m === "application/rtf" ||
                                n.endsWith(".txt") || n.endsWith(".rtf");
                              if (!isSupported) {
                                toast.error("This attachment type is not supported by AI Onboard Crew.");
                                return;
                              }
                              navigate("/ai-crew-onboarding", {
                                state: {
                                  inboxAttachments: [{
                                    attachment_id: att.id,
                                    message_id: selectedMessage.id,
                                    original_filename: att.original_filename,
                                    mime_type: att.mime_type || "application/octet-stream",
                                  }],
                                },
                              });
                            }}
                            data-testid={`onboard-attachment-${att.id}`}
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            Onboard
                          </Button>
                          {/* EVENTSFLO-INBOX-PDF-INVOICE-REVIEW-UI-V1: per-attachment invoice extractor */}
                          {(() => {
                            const m = (att.mime_type || '').toLowerCase();
                            const n = (att.original_filename || '').toLowerCase();
                            const isPdf = m === 'application/pdf' || n.endsWith('.pdf');
                            if (!isPdf) return null;
                            return (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-emerald-300"
                                onClick={() => {
                                  setInvoiceImportAttachment({
                                    ...att,
                                    _message_id: selectedMessage.id,
                                  });
                                  setInvoiceImportOpen(true);
                                }}
                                data-testid={`extract-invoice-attachment-${att.id}`}
                                title="Extract Project from Invoice PDF"
                              >
                                <File className="w-3 h-3 mr-1" />
                                Extract invoice
                              </Button>
                            );
                          })()}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              // EVENTSFLO-CREW-ID-PHOTO-DETECTION-AND-INBOX-DOWNLOAD-FIX-V1:
                              // Force browser save (Content-Disposition: attachment)
                              // via programmatic <a download> click instead of
                              // window.open which renders the file inline for
                              // image/PDF MIME types.
                              const url = withInboxAttachmentToken(
                                `${API_URL}/api/inbox/attachments/${att.id}/file?disposition=attachment`
                              );
                              // EVENTSFLO-INBOX-ATTACHMENT-DOWNLOAD-FILENAME-V1:
                              // Match the backend Content-Disposition filename:
                              //   {senderEmail}+{originalFilename}.ext
                              // Same-origin <a download> overrides the
                              // server's filename, so we compute it here too.
                              const rawSender = String(selectedMessage?.sender || "").trim();
                              const m = rawSender.match(/<([^>]+)>/);
                              const senderEmail = (m ? m[1] : rawSender)
                                .toLowerCase()
                                .replace(/[^a-z0-9._%+\-@]/g, "");
                              const validEmail = senderEmail.includes("@") ? senderEmail : "";
                              const orig = att?.original_filename || att?.filename || "attachment";
                              const downloadName = validEmail ? `${validEmail}+${orig}` : orig;
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = downloadName;
                              a.rel = "noopener";
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            }}
                            data-testid={`download-attachment-${att.id}`}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Attachment count badge for messages without loaded attachments */}
              {selectedMessage.attachment_count > 0 && (!selectedMessage.attachments || selectedMessage.attachments.length === 0) && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Paperclip className="w-3 h-3" />
                    Attachments
                  </label>
                  <Badge className="mt-1 bg-blue-500/10 text-blue-400 border-blue-500/20">
                    {selectedMessage.attachment_count} attachment{selectedMessage.attachment_count > 1 ? 's' : ''}
                  </Badge>
                </div>
              )}
            </div>
            {/* EF-INBOX-UX-REFACTOR-V1: Footer removed - all actions now in header */}
          </>
          ) : (
            /* EF-INBOX-PREVIEW-ANCHOR-FIX-V1: Placeholder when no email selected */
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
              <Mail className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">Select an email to preview</h3>
              <p className="text-sm text-muted-foreground/70 max-w-[280px]">
                Click on any email from the list to view its contents and take actions
              </p>
            </div>
          )}
        </div>
      </div>
      </>
      )}
      
      {/* Context Menu */}
      {contextMenu.show && contextMenu.message && (
        <div
          className="fixed bg-card border border-white/10 rounded-sm shadow-xl py-1 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-surface-elevated flex items-center gap-2"
            onClick={() => handlePreviewParse(contextMenu.message)}
          >
            <Eye className="w-4 h-4" />
            Preview Parse
          </button>
          {/* EF-AI-EMAIL-INTERPRETER-V1: AI Interpreter context menu item */}
          <button
            className="w-full px-4 py-2 text-left text-sm text-purple-400 hover:bg-purple-500/10 flex items-center gap-2"
            onClick={() => {
              setAiInterpreterMessage(contextMenu.message);
              setShowAIInterpreter(true);
              setContextMenu({ show: false, x: 0, y: 0, message: null });
            }}
            data-testid="context-menu-ai-interpreter"
          >
            <Bot className="w-4 h-4" />
            AI Interpreter
            <Badge variant="outline" className="ml-auto text-[9px] h-4 px-1 border-purple-500/30">Beta</Badge>
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-surface-elevated flex items-center gap-2"
            onClick={() => handleCreateProject(contextMenu.message)}
          >
            <FileText className="w-4 h-4" />
            Create Project
          </button>
          <div className="border-t border-white/5 my-1" />
          {/* EF-EMAIL-INBOX-STATE-MANAGEMENT-V1: Mark as read/unread */}
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-surface-elevated flex items-center gap-2"
            onClick={() => {
              handleToggleRead(contextMenu.message.id, contextMenu.message.is_read);
              setContextMenu({ show: false, x: 0, y: 0, message: null });
            }}
            data-testid="context-menu-toggle-read"
          >
            {contextMenu.message.is_read ? (
              <>
                <Mail className="w-4 h-4" />
                Mark as Unread
              </>
            ) : (
              <>
                <MailOpen className="w-4 h-4" />
                Mark as Read
              </>
            )}
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm text-emerald-500 hover:bg-surface-elevated flex items-center gap-2"
            onClick={() => updateStatus(contextMenu.message.id, 'processed')}
          >
            <Check className="w-4 h-4" />
            Mark Processed
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm text-muted-foreground hover:bg-surface-elevated flex items-center gap-2"
            onClick={() => updateStatus(contextMenu.message.id, 'dismissed')}
          >
            <X className="w-4 h-4" />
            Dismiss
          </button>
          {/* EVENTSFLO-INBOX-BATCH-HIDE-CONTEXT-MENU-V1: Hide (single or batch) */}
          {!(contextMenu.message?.is_hidden === true || contextMenu.message?.status === 'hidden') && (
            <button
              className="w-full px-4 py-2 text-left text-sm text-amber-300 hover:bg-amber-500/10 flex items-center gap-2"
              onClick={() => {
                const useBatch =
                  selectedIds.size > 1 && selectedIds.has(contextMenu.message.id);
                setContextMenu({ show: false, x: 0, y: 0, message: null });
                if (useBatch) {
                  handleBatchHide();
                } else {
                  updateStatus(contextMenu.message.id, 'hidden');
                }
              }}
              data-testid="ctx-menu-hide"
            >
              <EyeOff className="w-4 h-4" />
              {selectedIds.size > 1 && selectedIds.has(contextMenu.message.id)
                ? `Hide selected (${selectedIds.size})`
                : 'Hide'}
            </button>
          )}
          {/* EVENTSFLO-INBOX-HIDDEN-VIEW-AND-UNHIDE-V1: Unhide */}
          {(contextMenu.message?.is_hidden === true || contextMenu.message?.status === 'hidden') && (
            <button
              className="w-full px-4 py-2 text-left text-sm text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-2"
              onClick={() => {
                const id = contextMenu.message.id;
                setContextMenu({ show: false, x: 0, y: 0, message: null });
                handleUnhide(id);
              }}
              data-testid="ctx-menu-unhide"
            >
              <Eye className="w-4 h-4" />
              Unhide
            </button>
          )}
          <div className="border-t border-white/5 my-1" />
          <button
            className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2"
            onClick={() => {
              setDeleteConfirm({ show: true, message: contextMenu.message });
              setContextMenu({ show: false, x: 0, y: 0, message: null });
            }}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
      
      {/* Preview Parse Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              Parsed Project Preview
            </DialogTitle>
            <DialogDescription>
              Review the extracted data before creating the project.
            </DialogDescription>
          </DialogHeader>
          
          {/* EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Format guidance */}
          <ImportFormatHintPanel className="mb-4" />
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : previewData ? (
            <div className="space-y-4 py-4">
              {/* EF-INBOX-PREVIEW-PARSE-INTEGRATION-FIX-V1: Access parsed data correctly */}
              {(() => {
                const parsed = previewData.parsed || previewData;
                return (
                  <>
                    <div className="bg-surface-elevated rounded p-4">
                      <h4 className="text-sm font-medium mb-2">Project</h4>
                      <p className="text-foreground">{parsed.project_name || 'Untitled'}</p>
                      {parsed.client_name && (
                        <p className="text-sm text-muted-foreground">Client: {parsed.client_name}</p>
                      )}
                      {parsed.start_date && parsed.end_date && (
                        <p className="text-sm text-muted-foreground">
                          {parsed.start_date} → {parsed.end_date}
                        </p>
                      )}
                    </div>
                    
                    {parsed.shifts?.length > 0 && (
                      <div className="bg-surface-elevated rounded p-4">
                        {/* EF-SHIFT-IMPORT-UI-RESTORE-TABBED-V1: Calculate shifts from date range */}
                        {(() => {
                          let shiftCount = parsed.total_shifts || parsed.shifts.length;
                          let totalCrew = parsed.total_crew || 0;
                          
                          // Calculate from project date range if available
                          if (parsed.start_date && parsed.end_date) {
                            const start = new Date(parsed.start_date);
                            const end = new Date(parsed.end_date);
                            const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                            if (daysDiff > 0) {
                              shiftCount = daysDiff;
                              // Recalculate total crew
                              const crewPerShift = parsed.shifts[0]?.staff_required || 0;
                              totalCrew = crewPerShift * shiftCount;
                            }
                          }
                          
                          return (
                            <>
                              <h4 className="text-sm font-medium mb-2">
                                Shifts ({shiftCount})
                              </h4>
                              <ul className="space-y-1 text-sm">
                                {parsed.shifts.slice(0, 5).map((shift, idx) => (
                                  <li key={idx} className="text-muted-foreground">
                                    <span className="font-medium text-foreground">{shift.name}</span>
                                    <br />
                                    {shift.date} • {shift.start_time} - {shift.end_time} • {shift.staff_required} crew
                                  </li>
                                ))}
                                {parsed.shifts.length > 5 && (
                                  <li className="text-muted-foreground italic">
                                    ...and {parsed.shifts.length - 5} more shifts
                                  </li>
                                )}
                              </ul>
                              <p className="text-sm text-muted-foreground mt-2">
                                Total: {shiftCount} shifts, {totalCrew} crew
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* EF-INBOX-SHIFT-IMPORT-WORKFLOW-V1: Show duplicate instruction as informational */}
                    {parsed.duplicate_instruction_detected && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4">
                        <h4 className="text-sm font-medium mb-2 text-blue-400">Duplicate Instruction Detected</h4>
                        <p className="text-sm text-blue-300">
                          Email requests: {parsed.duplicate_instruction_detected} days
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Note: System will determine duplication from project date range.
                        </p>
                      </div>
                    )}
                    
                    {/* EF-INBOX-SHIFT-IMPORT-WORKFLOW-V1: Show rule-managed supplements */}
                    {parsed.rule_managed_supplements_detected?.length > 0 && (
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded p-4">
                        <h4 className="text-sm font-medium mb-2 text-purple-400">Rule-Managed Supplements Detected</h4>
                        <ul className="space-y-1 text-sm text-purple-300">
                          {parsed.rule_managed_supplements_detected.map((s, idx) => (
                            <li key={idx}>• {s}</li>
                          ))}
                        </ul>
                        <p className="text-xs text-muted-foreground mt-1">
                          These will be applied automatically by system rules.
                        </p>
                      </div>
                    )}
                    
                    {parsed.warnings?.length > 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded p-4">
                        <h4 className="text-sm font-medium mb-2 text-amber-500">Warnings</h4>
                        <ul className="space-y-1 text-sm text-amber-400">
                          {parsed.warnings.map((w, idx) => (
                            <li key={idx}>• {w}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {parsed.confidence && Object.keys(parsed.confidence).length > 0 && (
                      <div className="bg-surface-elevated rounded p-4">
                        <h4 className="text-sm font-medium mb-2">Confidence</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(parsed.confidence).map(([field, level]) => (
                            <span 
                              key={field}
                              className={`px-2 py-1 rounded text-xs ${
                                level === 'HIGH' ? 'bg-green-500/20 text-green-400' :
                                level === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {field}: {level}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-muted-foreground py-4">No preview data available.</p>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            {previewData && (
              <>
                {/* EF-SHIFT-IMPORT-MULTI-SHIFT-UI-V1: Multi-shift import button */}
                <Button 
                  variant="outline"
                  onClick={async () => {
                    // Call multi-shift parser
                    setShowPreviewDialog(false);
                    try {
                      // Use body_raw or body_preview from selectedMessage
                      const emailBody = selectedMessage?.body_raw || selectedMessage?.body_preview || previewData?.raw_text || '';
                      const response = await api.post('/projects/parse-multi-shift', { text: emailBody });
                      setMultiShiftParsedData(response.data);
                      setImportMessageId(selectedMessage?.id);
                      setShowMultiShiftImportForm(true);
                    } catch (err) {
                      console.error('Multi-shift parse failed:', err);
                      toast.error('Failed to parse for multiple shifts');
                    }
                  }}
                  data-testid="multi-shift-import-btn"
                >
                  <ListChecks className="w-4 h-4 mr-2" />
                  Multi-Shift Import
                </Button>
                <Button 
                  onClick={() => {
                    // EF-INBOX-SHIFT-IMPORT-WORKFLOW-V1: Open import form instead of direct create
                    setShowPreviewDialog(false);
                    setImportParsedData(previewData.parsed || previewData);
                    setImportMessageId(selectedMessage?.id);
                    setShowImportForm(true);
                  }}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Import className="w-4 h-4 mr-2" />
                  Single Shift Import
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Project Confirmation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              Create Project
            </DialogTitle>
            <DialogDescription>
              This will parse the email and create a new project with shifts.
            </DialogDescription>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-2 py-4">
              <p><strong>Subject:</strong> {selectedMessage.subject}</p>
              <p><strong>From:</strong> {selectedMessage.sender}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-primary text-primary-foreground"
              onClick={() => confirmCreateProject(false)}
            >
              Confirm & Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* EVENTSFLO-PROJECT-INTAKE-PASTED-TEXT-INTO-EXISTING-PROJECT-FLOW-V1
          Pasted-text intake dialog — saves the pasted message into the inbox
          so the user can run the SAME existing "Current Rules Draft" or
          "New AI Draft" buttons against it. Review-first; nothing is auto-created. */}
      <Dialog open={showPasteDialog} onOpenChange={setShowPasteDialog}>
        <DialogContent className="bg-card border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Paste Project Request
            </DialogTitle>
            <DialogDescription>
              Paste a project / shift request from WhatsApp, SMS, email, phone notes, or any other channel.
              It is saved into the Inbox so you can review and run <strong>Current Rules Draft</strong> or
              <strong> New AI Draft</strong> — same as an email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Source</label>
                <select
                  className="w-full h-9 rounded border border-white/10 bg-background text-sm px-2"
                  value={pasteSource}
                  onChange={(e) => setPasteSource(e.target.value)}
                  data-testid="paste-source-select"
                >
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="SMS">SMS</option>
                  <option value="Email">Email (copied)</option>
                  <option value="Phone Notes">Phone Notes</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Subject (optional)</label>
                <Input
                  value={pasteSubject}
                  onChange={(e) => setPasteSubject(e.target.value)}
                  placeholder="Auto-generated from first line if blank"
                  data-testid="paste-subject-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Pasted text</label>
              <Textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={10}
                placeholder={'e.g. "Need 4 stagehands at Royal Albert Hall on Saturday 14:00–22:00. Client: Live Nation."'}
                className="font-mono text-sm"
                data-testid="paste-text-textarea"
              />
              <p className="text-[11px] text-muted-foreground">
                {pasteText.length.toLocaleString()} / 50,000 characters
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPasteDialog(false)}
              disabled={pasting}
              data-testid="paste-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPastedText}
              disabled={pasting || !pasteText.trim()}
              data-testid="paste-submit-btn"
            >
              {pasting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Add to Inbox'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thread Duplicate Warning Dialog */}
      <Dialog open={!!threadWarning} onOpenChange={() => setThreadWarning(null)}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="text-amber-500 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Duplicate Project Warning
            </DialogTitle>
            <DialogDescription>
              {threadWarning?.message}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded">
              <p className="text-amber-200 text-sm">
                A project was already created from this email conversation.
              </p>
              {threadWarning?.existingEmailSubject && (
                <p className="text-muted-foreground text-xs mt-1">
                  Original email: "{threadWarning.existingEmailSubject}"
                </p>
              )}
            </div>
            
            {threadWarning?.existingProjectId && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  navigate(`/projects/${threadWarning.existingProjectId}`);
                  setThreadWarning(null);
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Existing Project
              </Button>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setThreadWarning(null)}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleForceCreate}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Create Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* EF-INBOX-SHIFT-IMPORT-WORKFLOW-V1: Shift Import Form */}
      <ShiftImportForm
        isOpen={showImportForm}
        onClose={() => {
          setShowImportForm(false);
          setImportParsedData(null);
          setImportMessageId(null);
        }}
        parsedData={importParsedData}
        messageId={importMessageId}
        onSuccess={async () => {
          await loadMessages(activeTab);
        }}
      />
      
      {/* EF-SHIFT-IMPORT-MULTI-SHIFT-UI-V1: Multi-Shift Import Form */}
      <MultiShiftImportForm
        isOpen={showMultiShiftImportForm}
        onClose={() => {
          setShowMultiShiftImportForm(false);
          setMultiShiftParsedData(null);
          setImportMessageId(null);
        }}
        multiShiftData={multiShiftParsedData}
        messageId={importMessageId}
        onSuccess={async () => {
          await loadMessages(activeTab);
        }}
      />
      
      {/* EF-CREW-DOCUMENT-INGESTION-FIX-V1: Document Review Workspace */}
      <DocumentReviewWorkspace
        open={workspaceOpen}
        onOpenChange={setWorkspaceOpen}
        attachment={selectedAttachmentForReview}
        allAttachments={allMessageAttachments}
        onAssignComplete={() => {
          // Refresh the message to update attachment status
          if (selectedMessage?.id) {
            handleRowClick(selectedMessage);
          }
        }}
      />
      
      {/* EF-EMAIL-INBOX-STABILISATION-V1: Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, message: null })}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-secondary text-lg uppercase tracking-wide text-red-500">
              Delete Email
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this email and all its attachments?
            </DialogDescription>
          </DialogHeader>
          
          {deleteConfirm.message && (
            <div className="p-3 bg-surface-elevated/50 rounded-lg space-y-1 text-sm">
              <p className="font-medium truncate">{deleteConfirm.message.subject || 'No subject'}</p>
              <p className="text-muted-foreground text-xs">{deleteConfirm.message.sender}</p>
            </div>
          )}
          
          <p className="text-sm text-amber-500 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            This action cannot be undone.
          </p>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm({ show: false, message: null })}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMessage}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* EF-AI-EMAIL-INTERPRETER-V2 + EF-AI-BATCH-CREATE-V1: AI Interpreter Panel */}
      <AIInterpreterPanel
        open={showAIInterpreter}
        onClose={() => {
          setShowAIInterpreter(false);
          setAiInterpreterMessage(null);
          setAiInterpreterSelection('');
        }}
        emailText={aiInterpreterMessage?.body_raw || aiInterpreterMessage?.body_preview || ''}
        selectedText={aiInterpreterSelection}
        emailSubject={aiInterpreterMessage?.subject || ''}
        sourceMessageId={aiInterpreterMessage?.id}
        onProjectsCreated={(createdProjects) => {
          // Callback after successful creation
          console.log('[AI_INTERPRETER] Projects created:', createdProjects);
          toast.success(`Created ${createdProjects.length} project(s)`);
          // Refresh inbox or navigate to projects
        }}
      />

      {/* EVENTSFLO-INBOX-PDF-INVOICE-REVIEW-UI-V1 (Phase 3) */}
      <InvoiceImportReviewDialog
        open={invoiceImportOpen}
        onOpenChange={(o) => {
          setInvoiceImportOpen(o);
          if (!o) setInvoiceImportAttachment(null);
        }}
        attachment={invoiceImportAttachment}
        messageId={invoiceImportAttachment?._message_id}
      />

      {/* EVENTSFLO-INBOX-INTAKE-STRUCTURE-AND-HELP-V1: in-platform Help dialog */}
      <Dialog open={inboxHelpOpen} onOpenChange={setInboxHelpOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="inbox-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              How Inbox works
            </DialogTitle>
            <DialogDescription>
              Inbox is your <strong>Operations Intake Hub</strong>. Two main intake flows happen here.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 text-sm leading-relaxed">
            <section>
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-amber-400" />
                Crew Intake
              </h3>
              <p className="text-muted-foreground">
                Use Inbox to receive crew documents (passports, IDs, certificates, contracts) for
                onboarding and renewals. In the email's <em>Attachments</em> section below the body,
                use the per-attachment <strong>Onboard</strong> button — or the bulk
                <strong> Onboard Crew (N)</strong> button — to send the file(s) into AI Crew
                Onboarding without downloading and re-uploading.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                Project Intake
              </h3>
              <p className="text-muted-foreground mb-2">
                Use Inbox to turn project / shift requests and changes into drafts. Two paths are
                available side by side — pick the one that suits the email:
              </p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1.5">
                <li>
                  <Badge className="text-[10px] mr-1 bg-primary text-primary-foreground">
                    Current Rules Draft
                  </Badge>
                  the established operational path. Uses your live shift rules, allocation
                  engine and rule variables to create a project draft. This is the default for
                  routine work.
                </li>
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1 border-purple-500/40 text-purple-300">
                    New AI Draft
                  </Badge>
                  the newer AI interpretation path. Reads your tenant-managed AI Rules and
                  produces a structured draft with cited rule IDs, ambiguities and a confidence
                  score. <strong>Review-first — never blind auto-processing.</strong> Nothing is
                  saved until you confirm.
                </li>
              </ul>
              <p className="text-muted-foreground mt-2 text-xs">
                Both paths remain available. Neither is removed.
              </p>
            </section>

            {/* EVENTSFLO-AI-PARSER-HELP-REPORT-RULE-PREVIEW-V1 */}
            <section className="rounded-md border border-purple-500/30 bg-purple-500/5 p-3">
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <Bot className="h-4 w-4 text-purple-300" />
                AI Parser — full email vs. selected text
              </h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1.5">
                <li>
                  <strong>Full email:</strong> select an email in the Inbox list, then press
                  <Badge variant="outline" className="text-[10px] mx-1 border-purple-500/40 text-purple-300">New AI Draft</Badge>
                  to parse the entire email body.
                </li>
                <li>
                  <strong>Selected text only (recommended for accuracy):</strong> highlight just
                  the relevant block inside the email body — the dates / times / quantities — then
                  press <Badge variant="outline" className="text-[10px] mx-1 border-purple-500/40 text-purple-300">New AI Draft</Badge>.
                  The parser will use only that selection.
                </li>
                <li>
                  Why selected-text parsing matters: signatures, old reply threads, legal disclaimers,
                  attachments, calendar invites and unrelated chatter all distort the AI&rsquo;s structured
                  output. Highlighting only the operational block gives a much cleaner, more accurate draft.
                </li>
                <li>
                  A purple banner inside the AI Parser will confirm <em>&quot;Parsing selected text only&quot;</em>
                  with a one-click toggle to <em>&quot;Use full email&quot;</em>.
                </li>
              </ul>
            </section>

            <section className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <FileText className="h-4 w-4 text-emerald-300" />
                Paste Text — bring outside requests into Inbox
              </h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1.5">
                <li>
                  Use <Badge variant="outline" className="text-[10px] mx-1 border-emerald-500/40 text-emerald-300">Paste Text</Badge>
                  to drop in operational requests received via SMS, WhatsApp, another email account,
                  meeting notes or any other external source.
                </li>
                <li>
                  Pasted content lands as a normal message in your Inbox so every request lives
                  inside the same intake / audit trail before AI parsing.
                </li>
                <li>
                  After pasting, the new message is auto-selected — you can then press
                  <Badge variant="outline" className="text-[10px] mx-1 border-purple-500/40 text-purple-300">New AI Draft</Badge>
                  exactly like for an email, including the highlight-then-parse trick.
                </li>
              </ul>
            </section>

            <section className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3">
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-cyan-300" />
                Rules Applied report &amp; Preview Rules
              </h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1.5">
                <li>
                  After you create projects from an AI draft, the <strong>Rules Applied</strong>
                  panel lists the deterministic operational rules that fired on the backend —
                  Crew Chief, Meal / Lunch Allowance, Overtime, Meal-Break, Late-Night Safety,
                  Transport, plus any tenant-configured rules.
                </li>
                <li>
                  These results come from the platform rules engine, <strong>not from AI guesses</strong>,
                  and are the same rules a manually-created shift would trigger.
                </li>
                <li>
                  Use the <Badge variant="outline" className="text-[10px] mx-1 border-cyan-500/40 text-cyan-300">Preview Rules</Badge>
                  button next to <em>Create Project(s)</em> to see — before saving — which rules
                  are expected to apply and which line items they will generate. Generated entries
                  are styled distinctly and clearly labelled as <em>Rule-based</em>.
                </li>
                <li>
                  <strong>Bug surface:</strong> if the report shows a rule (e.g. Meal Allowance)
                  but the saved shift has no corresponding line item, that is a backend rule
                  application issue. Open the shift and check; an actionable warning will appear
                  in the <em>Action required</em> banner with the failing shift name and reason.
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-semibold mb-1">General actions</h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1">Review</Badge>
                  open an attachment in the document workspace.
                </li>
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1">Download</Badge>
                  save an attachment locally.
                </li>
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1">Parse</Badge>
                  quick AI parse preview of the email body.
                </li>
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1 text-emerald-300 border-emerald-500/40">
                    Done
                  </Badge>
                  marks the email as handled.
                </li>
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1 text-amber-300 border-amber-500/40">
                    Hide
                  </Badge>
                  for spam or irrelevant emails — kept in the database for audit, but removed
                  from your active queues. Different from <em>Dismiss</em> (reviewed but not
                  actioned) and <em>Delete</em> (permanent removal).
                </li>
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1">Dismiss</Badge>
                  reviewed but no further action needed.
                </li>
                <li>
                  <Badge variant="outline" className="text-[10px] mr-1 text-red-300 border-red-500/40">
                    Delete
                  </Badge>
                  permanent removal of the email and its attachments. Use sparingly.
                </li>
              </ul>
            </section>

            <section className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <Info className="h-4 w-4 text-amber-400" />
                AI paths are review-first
              </h3>
              <p className="text-muted-foreground">
                Drafts produced by either path must be reviewed before they create live records.
                The AI never silently creates projects or shifts — you always see, edit and
                confirm the draft first.
              </p>
            </section>
          </div>

          <DialogFooter>
            <Button onClick={() => setInboxHelpOpen(false)} data-testid="inbox-help-close">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </PageZoomContainer>
    </div>
  );
}
